/**
 * The bed the mission sits on (07 §1: "silence is a feature" - so the silence has to be made
 * of something): wind coloured by biome and weather, gusting on its own slow clock; rain; and
 * the hum of whatever is running nearby - a radar, a pumping station, a comms hut.
 */

import type { WindMix } from '../airMix';
import type { Spatial } from '../spatial';
import type { Graph } from './graph';

const GLIDE = 0.25;

export class Ambience {
  private readonly windBand: BiquadFilterNode;
  private readonly windGain: GainNode;
  private readonly whistleGain: GainNode;
  private readonly rainGain: GainNode;
  private readonly humGain: GainNode;
  private readonly humPan: StereoPannerNode | null;

  constructor(private readonly graph: Graph) {
    const ctx = graph.ctx;
    const silent = (): GainNode => {
      const g = ctx.createGain();
      g.gain.value = 0;
      return g;
    };
    const filter = (type: BiquadFilterType, hz: number, q: number): BiquadFilterNode => {
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = hz;
      f.Q.value = q;
      return f;
    };

    this.windBand = filter('bandpass', 600, 1);
    this.windGain = silent();
    graph.noiseSource().connect(this.windBand).connect(this.windGain).connect(graph.ambience);

    this.whistleGain = silent();
    graph.noiseSource().connect(filter('bandpass', 2300, 9)).connect(this.whistleGain).connect(graph.ambience);

    this.rainGain = silent();
    graph.noiseSource().connect(filter('highpass', 2600, 0.5)).connect(filter('lowpass', 9000, 0.5))
      .connect(this.rainGain).connect(graph.ambience);

    // Mains hum: the fundamental, its octave, and a little rasp, through the world bus so it is positional.
    this.humGain = silent();
    this.humPan = typeof ctx.createStereoPanner === 'function' ? ctx.createStereoPanner() : null;
    const humTone = filter('lowpass', 260, 0.7);
    for (const [type, hz, level] of [['sawtooth', 50, 0.5], ['sine', 100, 0.35]] as const) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = hz;
      const g = ctx.createGain();
      g.gain.value = level;
      o.connect(g).connect(humTone);
      o.start();
    }
    humTone.connect(this.humGain);
    (this.humPan ? this.humGain.connect(this.humPan) : this.humGain).connect(graph.world);
  }

  wind(m: WindMix, scale: number): void {
    const now = this.graph.now;
    // Make-up gain by arithmetic, not by ear: a 600 Hz band keeps about 4 % of white noise's power.
    this.windGain.gain.setTargetAtTime(m.gain * 3 * scale, now, GLIDE);
    this.windBand.frequency.setTargetAtTime(m.cutoff, now, GLIDE);
    this.windBand.Q.setTargetAtTime(m.q, now, GLIDE);
    this.whistleGain.gain.setTargetAtTime(m.whistle * 4 * scale, now, GLIDE);
    this.rainGain.gain.setTargetAtTime(m.rain * scale, now, GLIDE);
  }

  /** `s`: the nearest running machine's place in the mix, or null. */
  hum(s: Spatial | null): void {
    const now = this.graph.now;
    this.humGain.gain.setTargetAtTime(s ? s.gain * 0.12 : 0, now, GLIDE);
    if (this.humPan) this.humPan.pan.setTargetAtTime(s?.pan ?? 0, now, GLIDE);
  }

  silence(): void {
    const now = this.graph.now;
    const all = [this.windGain, this.whistleGain, this.rainGain, this.humGain];
    for (const g of all) g.gain.setTargetAtTime(0, now, 0.15);
  }
}
